import { Cat, KINDS } from '../../../gameplay/registry';
import { C } from '../../../gameplay/types';
import { FX_ONE } from '../fixed';
import { Order, UState, type World } from '../world';
import { approach, stopMoving } from './movement';

/** Defenders (the owner's or an ally's soldiers, towers and castles) this close stop a capture. */
export const CAPTURE_DEFENCE_RADIUS = 8 * FX_ONE;
/** How close an engineer must stand to the building's edge. */
const CAPTURE_REACH = (FX_ONE * 3) >> 2;

/** Buildings engineers may commandeer: any finished one except walls, gates and the Wonder. */
export function isCapturable(w: World, b: number): boolean {
  if (b < 0 || !w.alive[b] || w.cat[b] !== Cat.Building || !w.built[b] || w.owner[b] < 0) return false;
  const def = KINDS[w.kind[b]].building!;
  return !(def.classes & C.Wall) && KINDS[w.kind[b]].key !== 'wonder';
}

/** Whether `player` may start commandeering building b (an enemy's, capturable). */
export function canCapture(w: World, player: number, b: number): boolean {
  return isCapturable(w, b) && w.areEnemies(player, w.owner[b]);
}

/**
 * True while the building's owner, or an ally of theirs, has soldiers or a defensive building (a
 * tower or anything that shoots, such as a castle) within 8 tiles, or units inside it: the area is not
 * yet won.
 */
export function isDefended(w: World, b: number): boolean {
  // Units sheltering inside hold it.
  if (w.cargo[b]) return true;
  const owner = w.owner[b];
  const r2 = CAPTURE_DEFENCE_RADIUS * CAPTURE_DEFENCE_RADIUS;
  let defended = false;
  w.forUnitsInRadius(w.x[b], w.y[b], CAPTURE_DEFENCE_RADIUS + w.extent(b), (o) => {
    if (defended || !w.alive[o] || w.owner[o] < 0) return;
    if (w.owner[o] !== owner && w.areEnemies(owner, w.owner[o])) return;
    if (KINDS[w.kind[o]].unit!.classes & C.Military) defended = true;
  });
  if (defended) return true;
  for (let s = 0; s < w.highSlot; s++) {
    if (s === b || !w.alive[s] || w.cat[s] !== Cat.Building || !w.built[s] || w.owner[s] < 0) continue;
    if (w.owner[s] !== owner && w.areEnemies(owner, w.owner[s])) continue;
    const def = KINDS[w.kind[s]].building!;
    if (!(def.classes & C.Tower) && !def.attack) continue;
    const dx = w.x[s] - w.x[b];
    const dy = w.y[s] - w.y[b];
    if (dx * dx + dy * dy <= r2) return true;
  }
  return false;
}

/** Hit points shown while a capture runs: down to nothing as the old owner's, then back up as the new owner's. */
function captureHp(hp0: number, prog: number, total: number): number {
  const half = total >> 1;
  const f = prog < half ? 1 - prog / half : (prog - half) / Math.max(1, total - half);
  return Math.max(1, Math.trunc(hp0 * f));
}

/**
 * Engineers commandeering buildings. A capture takes half the building's build time (sooner with
 * more engineers, with diminishing returns as for building), pauses while defenders are near, and is
 * called off, the building's health restored, if no engineer is working on it any more. The building
 * never falls: on completion it simply changes hands.
 */
export function updateCapture(w: World): void {
  // Which engineers are at work on which building this tick.
  const workers = new Map<number, { player: number; n: number }>();
  for (let s = 0; s < w.highSlot; s++) {
    if (!w.alive[s] || w.cat[s] !== Cat.Unit || w.order[s] !== Order.Capture) continue;
    const b = w.resolve(w.target[s]);
    const me = w.owner[s];
    if (!canCapture(w, me, b)) {
      w.order[s] = Order.None;
      w.state[s] = UState.Idle;
      continue;
    }
    if (w.edgeDist(b, w.x[s], w.y[s]) > CAPTURE_REACH) {
      if (!w.moving[s] && !w.paths[s]) approach(w, s, b);
      w.state[s] = UState.Walking;
      continue;
    }
    if (w.moving[s]) stopMoving(w, s);
    w.state[s] = UState.Building;
    const dx = w.x[b] - w.x[s];
    const dy = w.y[b] - w.y[s];
    const dd = Math.max(1, Math.floor(Math.sqrt(dx * dx + dy * dy)));
    w.fdx[s] = Math.trunc((dx * 1000) / dd);
    w.fdy[s] = Math.trunc((dy * 1000) / dd);
    const cur = workers.get(b);
    // The first side at work keeps the building; anyone else waits their turn.
    if (!cur) workers.set(b, { player: me, n: 1 });
    else if (cur.player === me) cur.n++;
  }
  for (let b = 0; b < w.highSlot; b++) {
    if (!w.alive[b] || w.cat[b] !== Cat.Building || w.captureBy[b] === 0) continue;
    const job = workers.get(b);
    if (!job || job.player + 1 !== w.captureBy[b]) {
      // Called off: the building is its owner's again, as healthy as before.
      w.hp[b] = Math.min(w.maxHp[b], Math.max(w.hp[b], w.captureHp[b]));
      w.captureBy[b] = 0;
      w.captureProg[b] = 0;
    }
  }
  for (const [b, job] of workers) {
    if (isDefended(w, b)) continue;
    if (w.captureBy[b] === 0) {
      w.captureBy[b] = job.player + 1;
      w.captureProg[b] = 0;
      w.captureHp[b] = w.hp[b];
      w.events.push({ e: 'captureStart', id: w.idOf(b), kind: w.kind[b], owner: w.owner[b], by: job.player, x: w.x[b], y: w.y[b] });
    }
    const total = Math.max(2, (w.players[w.owner[b]].stats[w.kind[b]].buildTicks * 1000) >> 1);
    w.captureProg[b] = Math.min(total, w.captureProg[b] + Math.trunc((3000 * job.n) / (job.n + 2)));
    w.hp[b] = captureHp(w.captureHp[b], w.captureProg[b], total);
    if (w.captureProg[b] >= total) {
      const from = w.owner[b];
      w.transferBuilding(b, job.player);
      w.hp[b] = Math.min(w.maxHp[b], w.captureHp[b]);
      w.captureBy[b] = 0;
      w.captureProg[b] = 0;
      w.events.push({ e: 'captured', id: w.idOf(b), kind: w.kind[b], from, to: job.player });
      for (let s = 0; s < w.highSlot; s++) {
        if (w.alive[s] && w.order[s] === Order.Capture && w.resolve(w.target[s]) === b) {
          w.order[s] = Order.None;
          w.state[s] = UState.Idle;
        }
      }
    }
  }
}

/** Fraction of a capture done (0 to 1), or -1 if the building is not being captured. */
export function captureProgress(w: World, b: number): number {
  if (!w.captureBy[b]) return -1;
  const total = Math.max(2, (w.players[w.owner[b]].stats[w.kind[b]].buildTicks * 1000) >> 1);
  return Math.min(1, w.captureProg[b] / total);
}
